import type {LocationQuery} from 'vue-router'
import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'
import {REQUIRED_FIELD_COUNTS} from '@/components/www/listing-rejections/listingRejectionsLp'
import {formatPrice, lowestPricePerMonth} from '@/components/www/pricing/pricingData'
import type {LandingComparison, LandingComparisonRow} from '@/components/www/landing/landingComparison'
import type {LandingFaqItem} from '@/components/www/landing/landingFaq'
import {hrefWithCampaignParams} from '@/lib/campaignParams'
import competitorsJson from './competitors.json'

/**
 * Paid-search landing pages: /fashion-marketplace-listing-alternatives (generic "alternatives to X"
 * destination) and /compare/:slug (one per competitor, data-driven from competitors.json).
 *
 * Copy rules these pages are bound by (see the dev brief in the PR that added them):
 * - competitor names are plain text in a factual comparison; never a logo, wordmark or screenshot;
 * - every competitor cell is sourced from that company's own public pages, or left blank;
 * - no superlatives, nothing disparaging, no invented customers or figures. Every number here is
 *   already published elsewhere on osello.com (case studies, pricing, listing-rejections LP).
 */

export const ALTERNATIVES_PATH = '/fashion-marketplace-listing-alternatives'
export const COMPARE_PATH = '/compare'
export const FEATURES_PATH = '/features'
export const PRICING_PATH = '/pricing'

/** `competitor_slug` pushed to the dataLayer for the generic alternatives page. */
export const ALTERNATIVES_ANALYTICS_SLUG = 'alternatives'

/** Column ids of the comparison table; the keys of `Competitor['table']`. */
export type CompareRowKey =
    | 'fashion'
    | 'aiListings'
    | 'imageEditing'
    | 'colorways'
    | 'mapping'
    | 'requirements'
    | 'shopify'
    | 'timeToApproval'
    | 'startingPrice'

export interface CompetitorSource {
    label: string
    url: string
}

export interface Competitor {
    slug: string
    name: string
    /** Previous company name worth acknowledging in copy, e.g. Rithum's ChannelAdvisor. */
    formerly?: string
    /** ISO date the competitor's public pages were last read. Rendered in the disclaimer. */
    verifiedOn: string
    /** What they say about themselves, in our words, with their phrases quoted. */
    description: string
    sources: CompetitorSource[]
    /** Their cell per comparison row. Empty string renders blank; never guess. */
    table: Record<CompareRowKey, string>
}

interface CompetitorJson extends Omit<Competitor, 'slug'> {}

const COMPETITORS_BY_SLUG: Record<string, Competitor> = Object.fromEntries(
    Object.entries(competitorsJson.competitors as Record<string, CompetitorJson>).map(([slug, data]) => [slug, {slug, ...data}]),
)

export const COMPETITOR_SLUGS: readonly string[] = Object.keys(COMPETITORS_BY_SLUG)

export function findCompetitor(slug: string): Competitor | undefined {
    return COMPETITORS_BY_SLUG[slug]
}

export function comparePath(slug: string): string {
    return `${COMPARE_PATH}/${slug}`
}

/**
 * Demo-request link for a CTA on these pages: carries the paid click's `gclid` / `utm_*` through
 * (see lib/campaignParams.ts) and tags the source so the two page types stay separable.
 */
export function compareDemoHref(query: LocationQuery, source: string, cta: string): string {
    return hrefWithCampaignParams(DEMO_REQUEST_PATH, query, {source, cta})
}

/** Secondary CTA: /features, still carrying the campaign params so a later demo request attributes. */
export function compareFeaturesHref(query: LocationQuery): string {
    return hrefWithCampaignParams(FEATURES_PATH, query)
}


export const STARTING_PRICE = formatPrice(lowestPricePerMonth())

export const RETAILERS_PHRASE = "Nordstrom, Macy's, Bloomingdale's and Target Plus"

/** Long-form date for the disclaimer, e.g. "September 15, 2026". Rendered in UTC so SSR and client agree. */
export function formatVerifiedOn(isoDate: string): string {
    const [year, month, day] = isoDate.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    })
}

export function trademarkDisclaimer(competitor: Competitor): string {
    return `${competitor.name} is a trademark of its respective owner. Osello is not affiliated with or endorsed by ${competitor.name}. Comparison based on publicly available information as of ${formatVerifiedOn(competitor.verifiedOn)}, and may not reflect their current offering.`
}

// --- Shared copy --------------------------------------------------------------------------------

export interface ProblemPoint {
    id: string
    lead: string
    body: string
}

/**
 * The problem, concretely. Field counts are the client-supplied figures already quoted on
 * /listing-rejections (REQUIRED_FIELD_COUNTS); the image and colorway points restate the
 * homepage's own claims.
 */
export const PROBLEM_POINTS: readonly ProblemPoint[] = [
    {
        id: 'fields',
        lead: `Nordstrom publishes ${REQUIRED_FIELD_COUNTS.nordstrom} required fields. Macy's publishes ${REQUIRED_FIELD_COUNTS.macys}.`,
        body: 'Size type, size standard, NRF color code, material composition percentage, country of manufacture. None of them exist in Shopify, and each retailer names them differently.',
    },
    {
        id: 'images',
        lead: 'The same photo passes one retailer and fails the next.',
        body: 'Each marketplace sets its own aspect ratio, background and alternate-image rules. A catalog shot for your own site needs re-cropping and re-backgrounding for every destination.',
    },
    {
        id: 'colorways',
        lead: 'One dress in six colors is six Shopify products. The marketplace wants one listing.',
        body: 'Shopify stores split colorways into separate products. Retailers require one style with six colorways and a full size run under each, or the style is rejected.',
    },
]

export interface Differentiator {
    id: string
    title: string
    proof: string
}

/** Every differentiator, in priority order. The generic page shows all; competitor pages a subset. */
export const DIFFERENTIATORS: readonly Differentiator[] = [
    {
        id: 'ai-agents',
        title: 'AI agents build and optimize your listings',
        proof: 'You connect Shopify. Instead of you configuring feed rules, an agent writes each retailer\'s required fields per listing and asks you when it isn\'t sure.',
    },
    {
        id: 'image-editing',
        title: 'Agentic image editing',
        proof: 'Backgrounds whitened, aspect ratios corrected per marketplace, alternate images reordered. No re-shoot, no retouching queue.',
    },
    {
        id: 'approval-speed',
        title: 'Approved in a fraction of the time',
        proof: 'Before Osello, brands report 80 days to a first listing. Universal Standard\'s first live listing came 5 days after install, with 93% approved on first submission.',
    },
    {
        id: 'fashion',
        title: 'Built for fashion brands, not everyone',
        proof: 'Apparel, footwear, accessories and baby. Size runs, size types, colorways and category-specific fields are the default, not a configuration.',
    },
    {
        id: 'control',
        title: 'Full control over listings',
        proof: 'Every listing is a stored record you can open, edit and preview as shoppers will see it before it publishes.',
    },
    {
        id: 'modern',
        title: 'Built now, for how this works now',
        proof: 'Most of this category was architected as feed and EDI plumbing a decade or two ago. Osello was built for agents that do the work, not rules you maintain.',
    },
    {
        id: 'merge-split',
        title: 'Merge or split Shopify products into marketplace listings',
        proof: 'One Shopify product can become several listings, and several can become one. The mapping is automatic and you can override it per style.',
    },
    {
        id: 'colorways',
        title: 'Combine color-separated products into one listing',
        proof: 'Six Shopify products for the same dress become one marketplace listing with six colorways and every size under each.',
    },
    {
        id: 'requirements',
        title: 'Retailer-specific requirements built in',
        proof: `We maintain the listing requirement sets for ${RETAILERS_PHRASE}. You never learn one retailer's attribute schema, let alone four.`,
    },
    {
        id: 'sync',
        title: 'Live inventory, price and order sync',
        proof: 'Inventory and prices push from Shopify hourly. Marketplace orders land in Shopify as orders, and fulfillments flow back.',
    },
    {
        id: 'expansion',
        title: 'Add a retailer without rebuilding your catalog',
        proof: 'The listings built for your first marketplace carry to the next one. Adding Macy\'s after Nordstrom is a connection, not a project.',
    },
    {
        id: 'onboarding',
        title: 'Real onboarding, not a ticket queue',
        proof: 'A founder is on your onboarding calls. You get a person who knows your catalog, not a support SLA.',
    },
]

/** Which differentiators a competitor page shows, in order. */
export const COMPETITOR_PAGE_DIFFERENTIATOR_IDS: readonly string[] = [
    'ai-agents', 'image-editing', 'approval-speed', 'fashion', 'colorways', 'onboarding',
]

export function differentiatorsFor(ids: readonly string[]): Differentiator[] {
    return ids.map((id) => {
        const found = DIFFERENTIATORS.find((item) => item.id === id)
        if (!found) throw new Error(`Unknown differentiator: ${id}`)
        return found
    })
}

/** Row labels and Osello's answer, shared by every competitor table. */
export const COMPARE_ROWS: ReadonlyArray<{key: CompareRowKey, label: string, osello: string}> = [
    {key: 'fashion', label: 'Fashion and apparel specialization', osello: 'Only fashion: apparel, footwear, accessories and baby, on department-store marketplaces'},
    {key: 'aiListings', label: 'AI agent listing creation', osello: 'An agent writes each listing\'s required fields and asks you when it isn\'t sure'},
    {key: 'imageEditing', label: 'Agentic image editing', osello: 'Backgrounds, aspect ratios and image order corrected per marketplace, automatically'},
    {key: 'colorways', label: 'Colorway and variant consolidation', osello: 'Color-separated Shopify products combine into one listing with a full size run per colorway'},
    {key: 'mapping', label: '1-to-many and many-to-1 mapping', osello: 'Automatic merge or split of Shopify products into listings, overridable per style'},
    {key: 'requirements', label: 'Retailer-specific requirement coverage', osello: `Maintained requirement sets for ${RETAILERS_PHRASE}`},
    {key: 'shopify', label: 'Shopify-native', osello: 'Shopify is the source of truth; nothing to export or re-key'},
    {key: 'timeToApproval', label: 'Time to first approved listing', osello: 'Universal Standard: 5 days from install to first live listing'},
    {key: 'startingPrice', label: 'Starting price', osello: `From ${STARTING_PRICE}/mo, flat, no revenue share`},
]

export function buildComparison(competitor: Competitor): LandingComparison {
    const rows: LandingComparisonRow[] = COMPARE_ROWS.map((row) => ({
        id: row.key,
        label: row.label,
        values: {osello: row.osello, competitor: competitor.table[row.key] ?? ''},
    }))

    return {
        eyebrow: 'Side by side',
        title: `Osello and ${competitor.name}, compared`,
        intro: `What ${competitor.name} says about itself on its own pages, next to what Osello does. A blank cell means their public pages don't say.`,
        columns: [
            {id: 'competitor', name: competitor.name, kicker: competitor.formerly ? `Formerly ${competitor.formerly}` : 'From their public pages'},
            {id: 'osello', name: 'Osello', kicker: 'Fashion marketplace listing', isUs: true, wordmark: true},
        ],
        rows,
        footnote: `${competitor.name} column: ${competitor.sources.map((source) => source.label).join(', ')}, read ${formatVerifiedOn(competitor.verifiedOn)}. Osello figures are from published case studies and the pricing page.`,
    }
}

export interface MigrationStep {
    id: string
    title: string
    body: string
}

/** Honest account of switching. Same commitments as the /listing-rejections continuity section. */
export const MIGRATION_STEPS: readonly MigrationStep[] = [
    {
        id: 'connect',
        title: 'Connect Shopify and your marketplace accounts',
        body: 'Osello reads your catalog from Shopify and your live listings from each retailer. Nothing is submitted yet.',
    },
    {
        id: 'build',
        title: 'Listings build alongside your current setup',
        body: 'Agents map every style to each retailer\'s requirements. You review the result against what is live today. Your existing listings keep selling throughout.',
    },
    {
        id: 'cutover',
        title: 'Cut over once the mapping is verified',
        body: 'Listed SKUs stay the same, so nothing duplicates on the marketplace. Orders keep landing in Shopify the way they already do. Then you cancel the old tool.',
    },
]

export const MIGRATION_NOTE = 'We will not put a date on your catalog before we have seen it. On the demo we walk through your live listings and tell you what the cutover would involve.'

export const FAQ_ITEMS: readonly LandingFaqItem[] = [
    {
        id: 'who',
        question: 'Who is Osello for?',
        answer: `Fashion brands on Shopify selling, or about to sell, on ${RETAILERS_PHRASE}. Apparel, footwear, accessories and baby. If you sell electronics or home goods, a horizontal platform will serve you better.`,
    },
    {
        id: 'ai',
        question: 'What does "AI agents build your listings" actually mean?',
        answer: 'Each retailer requires fields Shopify does not hold. An agent reads your product data and images and writes those fields for each listing. When it isn\'t sure enough of an answer, it leaves the field alone and asks you in the chat instead of guessing.',
    },
    {
        id: 'switch',
        question: 'Can we switch without taking listings down?',
        answer: 'Yes. Osello builds alongside your current setup and cuts over once the mapping is verified. Listed SKUs stay the same, so nothing duplicates on the marketplace and orders keep flowing.',
    },
    {
        id: 'cost',
        question: 'What does it cost?',
        answer: `Plans start at ${STARTING_PRICE} a month, flat, tiered by Active SKUs and marketplace volume. No revenue share, no per-order fee. The full table is on the pricing page.`,
    },
    {
        id: 'retailers',
        question: 'Which marketplaces do you support?',
        answer: `${RETAILERS_PHRASE}. The listings built for one carry to the next, so adding a retailer is a connection, not a rebuild.`,
    },
    {
        id: 'accuracy',
        question: 'How current is the competitor information?',
        answer: 'Every competitor fact comes from that company\'s own public pages, read on the date shown at the bottom of the page. Where their pages do not say, we leave the cell blank rather than guess.',
    },
]

/** FAQ items for the generic page: everything but the competitor-accuracy question. */
export const ALTERNATIVES_FAQ_ITEMS: readonly LandingFaqItem[] = FAQ_ITEMS.filter((item) => item.id !== 'accuracy')

export const ALTERNATIVES_LP = {
    meta: {
        title: 'Marketplace listing alternatives for fashion brands',
        description: `Comparing marketplace listing platforms? Osello is built only for fashion brands on Shopify selling on ${RETAILERS_PHRASE}. AI agents build the listings, from ${STARTING_PRICE}/mo.`,
    },
    hero: {
        eyebrow: 'Alternatives to feed and syndication tools',
        title: 'Your listings should not depend on a feed you maintain.',
        subhead: `Osello is the marketplace integration platform built only for fashion brands on Shopify selling on ${RETAILERS_PHRASE}. AI agents build each listing to the retailer's requirements, and you approve before it publishes.`,
        primaryCta: 'Book a demo',
        secondaryCta: 'See the features',
    },
    problem: {
        title: 'Why listings on department-store marketplaces are hard',
        intro: 'None of these problems are your product. All of them are the retailer\'s specification.',
    },
    differentiators: {
        title: 'What Osello does differently',
    },
    faq: {title: 'Questions, answered.'},
} as const

export function competitorPageCopy(competitor: Competitor) {
    return {
        meta: {
            title: `Osello vs. ${competitor.name}: marketplace listing for fashion brands`,
            description: `How Osello compares to ${competitor.name} for fashion brands on Shopify listing on ${RETAILERS_PHRASE}. Side-by-side table from ${competitor.name}'s own public pages, honest migration notes, pricing from ${STARTING_PRICE}/mo.`,
        },
        hero: {
            eyebrow: `Considering ${competitor.name}?`,
            title: 'Marketplace listing built only for fashion brands.',
            subhead: `${competitor.name} serves many kinds of sellers. Osello serves fashion brands on Shopify selling on ${RETAILERS_PHRASE}, with AI agents that build each listing to the retailer's requirements.`,
            primaryCta: 'Book a demo',
            secondaryCta: 'See the features',
        },
        about: {
            eyebrow: `About ${competitor.name}`,
            body: competitor.description,
        },
        problem: ALTERNATIVES_LP.problem,
        differentiators: {title: `Where Osello differs from ${competitor.name}`},
        migration: {
            eyebrow: 'Switching',
            title: `Moving from ${competitor.name} to Osello`,
        },
        faq: ALTERNATIVES_LP.faq,
    }
}
